import React, { useState, useEffect, useRef } from 'react';
import { Container, Nav, Navbar, Button, NavDropdown } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { Link, useLocation } from 'react-router-dom';
import { database } from '../firebase';
import { ref, onValue } from 'firebase/database';

// Touch-friendly style component
const TouchStyles: React.FC = () => {
  useEffect(() => {
    // Add the styles to the document head
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      /* Touch-friendly styles */
      .touch-nav-link, .touch-button, .touch-dropdown .dropdown-toggle, .touch-nav-item {
        padding: 10px 15px !important;
        margin: 2px 0 !important;
        min-height: 48px !important;
        display: flex !important;
        align-items: center !important;
      }
      
      .mobile-navbar {
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      }
      
      /* Add larger touch area for buttons */
      .touch-button {
        min-width: 100px;
      }
      
      /* Increase dropdown menu items touch area */
      .dropdown-menu {
        padding: 8px 0;
      }
      
      .dropdown-item {
        padding: 10px 15px;
        min-height: 48px;
      }
      
      /* Prevent text selection during touch interactions */
      .touch-nav-link, .touch-nav-item, .touch-button {
        -webkit-touch-callout: none;
        -webkit-user-select: none;
        user-select: none;
      }
      
      /* Add ripple effect for touch feedback */
      .touch-nav-link:active, .touch-nav-item:active, .touch-button:active {
        opacity: 0.7;
        transition: opacity 0.2s;
      }
    `;
    document.head.appendChild(styleElement);
    
    // Clean up
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);
  
  return null;
};

const AppNavbar = () => {
  const { currentUser, logout } = useAuth();
  const location = useLocation();
  const [userName, setUserName] = useState('');
  const [expanded, setExpanded] = useState(false);
  const navbarToggleRef = useRef<HTMLButtonElement>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Check if device is mobile (max-width: 768px)
  const [isMobile, setIsMobile] = useState(false);
  
  // Use window resize event to detect mobile devices
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Initial check
    checkMobile();
    
    // Add event listener
    window.addEventListener('resize', checkMobile);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  useEffect(() => {
    if (!currentUser) return;
    const userNameRef = ref(database, `user_settings/${currentUser.uid}/UserName`);
    const unsubscribe = onValue(userNameRef, (snapshot) => {
      const data = snapshot.val();
      setUserName(data || '');
    });
    return () => unsubscribe();
  }, [currentUser]);

  const navigationItems = [
    { path: '/dashboard', label: 'Home', icon: '🏠' },
    { 
      path: '/dashboard/vega', 
      label: 'VeGa', 
      icon: '🚲',
      subItems: [
        { path: '/dashboard/vega', label: 'Dashboard', icon: '📊' },
        { path: '/dashboard/vega/rides', label: 'Fahrten', icon: '🚴' },
        { path: '/dashboard/vega/settings', label: 'Konfiguration', icon: '⚙️' }
      ]
    },
    {
      path: '/bike',
      label: 'Bike',
      icon: '🚲',
      subItems: [
        { path: '/bike', label: 'Velos', icon: '🚲' },
        { path: '/bike/maintenance', label: 'Wartung', icon: '🔧' },
        { path: '/bike/settings', label: 'Konfiguration', icon: '⚙️' }
      ]
    },
    { 
      path: '/dashboard/moscht', 
      label: 'Moscht', 
      icon: '⛽',
      subItems: [
        { path: '/dashboard/moscht', label: 'Dashboard', icon: '📊' },
        { path: '/dashboard/moscht/tankungen', label: 'Tankungen', icon: '🛢️' },
        { path: '/dashboard/moscht/events', label: 'Events', icon: '📅' },
        { path: '/dashboard/moscht/logs', label: 'Logs', icon: '📝' },
        { path: '/dashboard/moscht/cars', label: 'Fahrzeuge', icon: '🚗' },
        { path: '/dashboard/moscht/settings', label: 'Konfiguration', icon: '⚙️' }
      ]
    },
    { 
      path: '/dashboard/work', 
      label: 'Work', 
      icon: '💼',
      subItems: [
        { path: '/dashboard/work', label: 'Dashboard', icon: '📊' },
        { path: '/dashboard/work-settings', label: 'Konfiguration', icon: '⚙️' }
      ]
    },
    {
      path: '/dashboard/learning-cards',
      label: 'Cards',
      icon: '🃏',
      subItems: [
        { path: '/dashboard/learning-cards/history', label: 'Learning History', icon: '📚' }
      ]
    },
    { path: '/dashboard/run', label: 'Run', icon: '🏃' },
    {
      path: '/dashboard/workouts',
      label: 'Workouts',
      icon: '💪',
      subItems: [
        { path: '/dashboard/workouts', label: 'Dashboard', icon: '📊' },
        { path: '/dashboard/workouts-list', label: 'Liste', icon: '📋' },
        { path: '/dashboard/workout-exercises', label: 'Übungen', icon: '🏋️' }
      ]
    },
    { path: '/dashboard/profile', label: 'Profile', icon: '👤' }
  ];

  const handleDropdownEnter = (e: React.MouseEvent<HTMLElement>) => {
    if (window.innerWidth >= 992) {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
      const dropdown = e.currentTarget;
      if (!dropdown.classList.contains('show')) {
        dropdown.click();
      }
    }
  };

  const handleDropdownLeave = (e: React.MouseEvent<HTMLElement>) => {
    if (window.innerWidth >= 992) {
      const dropdown = e.currentTarget;
      if (dropdown.classList.contains('show')) {
        hideTimeoutRef.current = setTimeout(() => {
          dropdown.click();
          hideTimeoutRef.current = null;
        }, 500);
      }
    }
  };

  return (
    <>
      {/* Top navbar - visible on all devices but with different appearance on mobile */}
      <Navbar 
        style={{ 
          backgroundColor: '#1780a6',
          paddingTop: isMobile ? '0.5rem' : '0.75rem',
          paddingBottom: isMobile ? '0.5rem' : '0.75rem' 
        }} 
        variant="dark" 
        expand="lg"
        expanded={expanded}
        onToggle={(expanded) => setExpanded(expanded)}
        className={isMobile ? 'mobile-navbar' : ''}
      >
        <Container fluid>
          <Navbar.Brand>Gruetze</Navbar.Brand>
          {/* Only show the toggle on tablet and above (md+) */}
          <div className="d-md-block d-none">
            <Navbar.Toggle ref={navbarToggleRef} aria-controls="basic-navbar-nav" />
          </div>
          {/* Logout button on mobile */}
          <div className="d-md-none d-flex align-items-center">
            <Button 
              variant="link" 
              className="text-light p-2 d-flex align-items-center"
              style={{ fontSize: '1.3rem' }}
              onClick={() => logout()}
              aria-label="Logout"
            >
              <span role="img" aria-label="Logout" style={{ fontWeight: 'bold' }}>🔓</span>
            </Button>
          </div>
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="me-auto">
              <Nav.Link 
                as={Link} 
                to="/dashboard"
                className={`${location.pathname === '/dashboard' ? 'active' : ''} touch-nav-link`}
                onClick={() => setExpanded(false)}
              >
                <span style={{ marginRight: '8px' }}>🏠</span>
                Home
              </Nav.Link>
              {navigationItems.slice(1).map(item => (
                item.subItems ? (
                  <NavDropdown
                    key={item.path}
                    title={
                      <span style={{ marginRight: '8px', cursor: 'pointer' }}>
                        {item.icon} {item.label}
                      </span>
                    }
                    id={`nav-dropdown-${item.label.toLowerCase()}`}
                    show={undefined}
                    onMouseEnter={handleDropdownEnter}
                    onMouseLeave={handleDropdownLeave}
                    className="touch-dropdown"
                  >
                    <NavDropdown.Item
                      as={Link}
                      to={item.path}
                      active={location.pathname === item.path}
                      onClick={() => setExpanded(false)}
                      style={{ fontWeight: 'bold' }}
                      className="touch-nav-item"
                    >
                      <span style={{ marginRight: '8px' }}>{item.icon}</span>
                      {item.label}
                    </NavDropdown.Item>
                    <NavDropdown.Divider />
                    {item.subItems.map(subItem => (
                      <NavDropdown.Item
                        key={subItem.path}
                        as={Link}
                        to={subItem.path}
                        active={location.pathname === subItem.path}
                        onClick={() => setExpanded(false)}
                        className="touch-nav-item"
                      >
                        <span style={{ marginRight: '8px' }}>{subItem.icon}</span>
                        {subItem.label}
                      </NavDropdown.Item>
                    ))}
                  </NavDropdown>
                ) : (
                  <Nav.Link 
                    key={item.path} 
                    as={Link} 
                    to={item.path}
                    className={`${location.pathname === item.path ? 'active' : ''} touch-nav-link`}
                    onClick={() => setExpanded(false)}
                  >
                    <span style={{ marginRight: '8px' }}>{item.icon}</span>
                    {item.label}
                  </Nav.Link>
                )
              ))}
            </Nav>
            <Nav>
              <Nav.Link 
                as={Link} 
                to="/dashboard/profile" 
                className="text-light d-flex align-items-center me-3 touch-nav-link"
                onClick={() => setExpanded(false)}
              >
                {userName || currentUser?.email}
              </Nav.Link>
              <Button 
                variant="outline-light" 
                onClick={() => {
                  setExpanded(false);
                  logout();
                }}
                className="touch-button"
              >
                Log Out
              </Button>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>
      
      {/* Bottom navigation is now handled by the Dashboard component */}
    </>
  );
};

// Export the NavbarWrapper as default
const NavbarWrapper = () => {
  return (
    <>
      <TouchStyles />
      <AppNavbar />
    </>
  );
};

export default NavbarWrapper;
